import type { IAuthUser } from '@today/types'
import { authConfig } from '@/auth/auth.config'
import { deriveCodeChallenge, generateCodeVerifier, generateState, stashPkce } from '@/auth/pkce'
import { apiClient } from './api.client'

/**
 * Auth service — the login boundary to our `@today/auth-service` backend.
 *
 * Login uses the Cognito hosted UI via authorization-code + PKCE:
 *   1. `initiateLogin()` redirects the browser to Cognito's `/oauth2/authorize`.
 *   2. Cognito redirects back to `/auth/callback?code&state`.
 *   3. `exchangeCallback()` posts the code + PKCE verifier to our BFF, which
 *      returns the Cognito access token (stored here) and keeps the Cognito
 *      refresh token in an HttpOnly cookie. `refresh()` trades that cookie for a
 *      new access token.
 *
 * OIDC config (hosted-UI domain, client id, scopes, redirect/logout URIs) lives
 * in `@/auth/auth.config`.
 */

/**
 * The hosted-UI `/oauth2/authorize` URL for an authorization-code + PKCE (S256)
 * request, optionally pinned to one identity provider.
 */
export function buildAuthorizeUrl(input: { challenge: string; state: string; identityProvider?: string }): string {
	const params = new URLSearchParams({
		client_id: authConfig.clientId,
		redirect_uri: authConfig.redirectUri,
		response_type: 'code',
		scope: authConfig.scopes,
		code_challenge: input.challenge,
		code_challenge_method: 'S256',
		state: input.state,
		...(input.identityProvider ? { identity_provider: input.identityProvider } : {}),
	})
	return `https://${authConfig.domain}/oauth2/authorize?${params.toString()}`
}

interface SessionResponse {
	accessToken: string
	user: IAuthUser
	/**
	 * The signed-in user must enrol a TOTP factor before any other authenticated
	 * surface will serve it (TO-612). The callback routes to forced enrolment when
	 * true, so a normal login — not only the invitation first-login — reaches
	 * enrolment instead of landing on a dead-end dashboard. Optional for
	 * backward/robustness: a response without it is treated as `false`.
	 */
	mfaEnrolmentRequired?: boolean
}

/**
 * Single-flight refresh: concurrent callers (e.g. parallel 401s from the API
 * client) share ONE `/auth/refresh` call so the single-use refresh cookie is
 * rotated exactly once. Returns the new access token, or null on failure.
 */
let refreshPromise: Promise<string | null> | null = null

export const authService = {
	/**
	 * Redirect to the Cognito hosted UI (authorization-code + PKCE S256 + state).
	 * A per-tenant login surface (TO-308) passes the `identityProvider` its tenant
	 * uses — `COGNITO` for native sign-in, or the tenant's SAML provider — so the
	 * hosted UI goes straight to that path instead of offering a chooser.
	 */
	initiateLogin: async (options: { identityProvider?: string } = {}): Promise<void> => {
		const verifier = generateCodeVerifier()
		const state = generateState()
		const challenge = await deriveCodeChallenge(verifier)
		stashPkce(verifier, state)
		window.location.assign(buildAuthorizeUrl({ challenge, state, identityProvider: options.identityProvider }))
	},

	/**
	 * Exchange the authorization code (+ PKCE verifier) for a session. The
	 * `redirectUri` must equal the one sent at authorize time; the backend always
	 * uses the configured Cognito client.
	 */
	exchangeCallback: async (input: { code: string; codeVerifier: string }): Promise<SessionResponse> => {
		const response = await apiClient.post<SessionResponse>('/auth/callback', {
			code: input.code,
			codeVerifier: input.codeVerifier,
			redirectUri: authConfig.redirectUri,
		})
		return response.data
	},

	/**
	 * Trade the refresh cookie for a fresh access token (silent re-auth), stored
	 * in localStorage. De-duplicated across concurrent callers. Returns the new
	 * access token, or null if refresh failed (caller should redirect to login).
	 */
	refresh: (): Promise<string | null> => {
		if (!refreshPromise) {
			refreshPromise = apiClient
				.post<SessionResponse>('/auth/refresh')
				.then((res) => {
					localStorage.setItem('accessToken', res.data.accessToken)
					return res.data.accessToken
				})
				.catch(() => null)
				.finally(() => {
					refreshPromise = null
				})
		}
		return refreshPromise
	},

	getCurrentUser: async (token: string): Promise<IAuthUser> => {
		const response = await apiClient.get<IAuthUser>('/auth/me', {
			headers: { Authorization: `Bearer ${token}` },
		})
		return response.data
	},

	/**
	 * Revoke the refresh token server-side, clear the local access token, then end
	 * the Cognito hosted-UI session — without that redirect the hosted-UI cookie
	 * would silently sign the user straight back in.
	 */
	logout: async (): Promise<void> => {
		try {
			await apiClient.post('/auth/logout')
		} catch {
			// Best-effort: a failed server revoke must not block local sign-out.
		} finally {
			localStorage.removeItem('accessToken')
		}
		const params = new URLSearchParams({ client_id: authConfig.clientId, logout_uri: authConfig.logoutUri })
		window.location.assign(`https://${authConfig.domain}/logout?${params.toString()}`)
	},
}
